import type { ConfigState, PageToRuntime, RuntimeToPage } from "idfweb-runtime-protocol";
import type { FsClient } from "idfweb-runtime-protocol/fs-rpc";
import { tools, type ToolRegistry } from "idfweb-tools";
import type { Workbench } from "idfweb-workbench";

import { describeChange, parseSettings, symbolTypes } from "./config-lines.ts";
import type { Closed, Guide } from "./guide.ts";

const CLOSED: Record<Closed, string> = {
    target: "the user clicked the control",
    button: "the user pressed Got It",
    escape: "the user pressed Escape",
    elsewhere: "the user clicked elsewhere on the page",
    replaced: "another card replaced it",
    gone: "the control disappeared from the page",
};

type ConfigAnswer = Omit<Extract<RuntimeToPage, { type: "config" }>, "type" | "id">;
type ConfigureAnswer = Omit<Extract<RuntimeToPage, { type: "configure" }>, "type" | "id">;

/** kconfserver requests the page sends the runtime worker for tool calls, and configures. */
export class PageConfigs {
    readonly #post: (message: PageToRuntime) => void;
    readonly #waiting = new Map<number, (answer: ConfigAnswer) => void>();
    readonly #configuring = new Map<number, (answer: ConfigureAnswer) => void>();
    #nextId = 1;

    constructor(post: (message: PageToRuntime) => void) {
        this.#post = post;
    }

    send(request: object): Promise<ConfigAnswer> {
        const id = this.#nextId++;
        return new Promise((resolve) => {
            this.#waiting.set(id, resolve);
            this.#post({ type: "config", id, request });
        });
    }

    /** Configure the project root's build directory unless a build would not; see `configure` in the protocol. */
    configure(): Promise<ConfigureAnswer> {
        const id = this.#nextId++;
        return new Promise((resolve) => {
            this.#configuring.set(id, resolve);
            this.#post({ type: "configure", id });
        });
    }

    /** True if the message was the answer to one of these requests. */
    receive(message: RuntimeToPage): boolean {
        if (message.type === "configure") {
            const { type: _type, id, ...answer } = message;
            this.#configuring.get(id)?.(answer);
            this.#configuring.delete(id);
            return true;
        }
        if (message.type !== "config") return false;
        const { type: _type, id, ...answer } = message;
        this.#waiting.get(id)?.(answer);
        this.#waiting.delete(id);
        return true;
    }
}

/**
 * The guide's operations as tools, for an agent the user connected: the
 * operations of `idfweb:` links, any palette command, and set_config. The
 * agent can already write files and flash, so it gets the whole palette
 * where a README gets a fixed list.
 */
export function addGuideTools(
    registry: ToolRegistry,
    deps: { guide: Guide; workbench: Workbench; configs: PageConfigs; fs: FsClient; buildDir: () => string },
): void {
    const { guide, workbench, configs, fs, buildDir } = deps;
    const result = (problem: string | undefined, done: string) =>
        problem === undefined ? { text: done } : { text: problem, isError: true };
    const pointed = async (shown: string | { closed: Promise<Closed> }, what: string, wait: boolean | undefined) => {
        if (typeof shown === "string") return { text: shown, isError: true };
        if (!wait) return { text: `The user sees ${what} pointed at.` };
        return { text: `The user saw ${what} pointed at; the card closed: ${CLOSED[await shown.closed]}.` };
    };

    registry.add(tools.listControls, async () => ({ text: guide.controls().join("\n") }));

    registry.add(tools.showControl, async ({ role, name, text, wait }) =>
        pointed(await guide.show(role, name, text), `${role} "${name}"`, wait),
    );

    registry.add(tools.showLines, async ({ path, lines, note }) => {
        const shown = await guide.open(path, lines, note ?? "");
        if (typeof shown === "string") return { text: shown, isError: true };
        const which = shown.first === shown.last ? `line ${shown.first}` : `lines ${shown.first}-${shown.last}`;
        return { text: `The user sees ${which} of ${path}.` };
    });

    registry.add(tools.showConfigOption, async ({ name, text, path, wait }) =>
        pointed(await guide.setting(name, text, path), `${name} in the sdkconfig editor`, wait),
    );

    registry.add(tools.runUiCommand, async ({ id }) => {
        if (id === undefined || workbench.command(id) === undefined) {
            const list = workbench
                .commands()
                .map(
                    (command) =>
                        `${command.id}: ${command.category === undefined ? "" : `${command.category}: `}${command.title}`,
                )
                .sort()
                .join("\n");
            const heading =
                id === undefined ? "The palette's commands:" : `There is no command ${id}. The palette's commands:`;
            return { text: `${heading}\n${list}`, isError: id !== undefined };
        }
        await workbench.execute(id);
        return { text: `Ran ${id}.` };
    });

    registry.add(tools.setLayout, async ({ preset, path }) =>
        result(
            await guide.layout(preset, path),
            `The layout is ${preset}${path === undefined ? "" : `, with ${path} open`}.`,
        ),
    );

    registry.add(tools.setConfig, async ({ settings }) => {
        // A project no build has configured is configured here, without a build.
        const started = Date.now();
        const configured = await configs.configure();
        if (configured.error !== undefined)
            return { text: `The project could not be configured: ${configured.error}`, isError: true };
        const note = configured.ran
            ? `Configured the project first, in ${Math.round((Date.now() - started) / 1000)} s.\n`
            : "";
        const menus = `${buildDir()}/config/kconfig_menus.json`;
        let bytes: Uint8Array;
        try {
            bytes = await fs.readFile(menus);
        } catch {
            return {
                text: `${menus} does not exist after the configure: this project has no ESP-IDF configuration.`,
                isError: true,
            };
        }
        const types = symbolTypes(JSON.parse(new TextDecoder().decode(bytes)) as Parameters<typeof symbolTypes>[0]);
        const parsed = parseSettings(settings, types);
        if ("error" in parsed) return { text: parsed.error, isError: true };
        const answer = await configs.send({ version: 3, set: Object.fromEntries(parsed.values), save: null });
        if (answer.error !== undefined) return { text: answer.error, isError: true };
        const reply = answer.reply as Parameters<typeof describeChange>[3];
        const described = describeChange(parsed.values, types, answer.before as ConfigState, reply, answer.sdkconfig!);
        return { ...described, text: note + described.text };
    });
}
